import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";
import api from "../../../utils/api";

export default function PatientProfile() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", dob: "", gender: "", bloodGroup: "",
    address: "", city: "", emergencyContact: "",
  });

  const { data, isLoading } = useQuery("patient-profile", () =>
    api.get("/patient/profile")
  );

  useEffect(() => {
    if (data?.profile) {
      const p = data.profile;
      setForm({
        name:             p.name              ?? "",
        email:            p.email             ?? "",
        phone:            p.phone             ?? "",
        dob:              p.dob               ?? "",
        gender:           p.gender            ?? "",
        bloodGroup:       p.blood_group       ?? "",  // snake_case → camelCase
        address:          p.address           ?? "",
        city:             p.city              ?? "",
        emergencyContact: p.emergency_contact ?? "",  // snake_case → camelCase
      });
    }
  }, [data]);

  const updateMutation = useMutation(
    () => api.put("/patient/profile", {
      ...form,
      blood_group:       form.bloodGroup,       // camelCase → snake_case for API
      emergency_contact: form.emergencyContact, // camelCase → snake_case for API
    }),
    { onSuccess: () => toast.success("Profile updated") }
  );

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate();
  };

  if (isLoading) return <div className="text-slate-500">Loading profile…</div>;

  return (
    <div className="max-w-2xl">
      <div className="card">
        <h2 className="font-display font-semibold text-slate-800 text-lg mb-6">
          Personal & Contact Details
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: "name", label: "Full Name" },
              { name: "email", label: "Email", type: "email" },
              { name: "phone", label: "Phone" },
              { name: "dob", label: "Date of Birth", type: "date" },
            ].map(({ name, label, type = "text" }) => (
              <div key={name}>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                  {label}
                </label>
                <input
                  type={type}
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                Gender
              </label>
              <select name="gender" value={form.gender} onChange={handleChange} className="input-field">
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                Blood Group
              </label>
              <select name="bloodGroup" value={form.bloodGroup} onChange={handleChange} className="input-field">
                <option value="">Select</option>
                {["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map((bg) => (
                  <option key={bg} value={bg}>{bg}</option>
                ))}
              </select>
            </div>
          </div>

          {[
            { name: "address", label: "Address" },
            { name: "city", label: "City" },
            { name: "emergencyContact", label: "Emergency Contact Phone" },
          ].map(({ name, label }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                {label}
              </label>
              <input
                type="text"
                name={name}
                value={form[name]}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          ))}

          <button
            type="submit"
            disabled={updateMutation.isLoading}
            className="btn-primary"
          >
            {updateMutation.isLoading ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}